<template>
  <FullScreenLayoutContent>
    <template #header>
      <div class="fixed top-0 right-0 left-0 flex h-16 items-center px-3 md:h-20 md:px-4">
        <a href="https://menon.group" target="_blank">
          <LumiaLogo class="w-40 p-4" />
        </a>
      </div>
    </template>
    <div class="relative flex h-screen w-full flex-col justify-center gap-6 px-4 py-8">
      <BaseShape
        v-if="step !== 'tasks' && step !== 'questions'"
        shape="flower"
        class="text-primary-container animate-rotate absolute top-[10vw] -left-[10vw] z-0 h-[15vw] min-h-[150px] w-[15vw] min-w-[150px] transition-all"
      />
      <BaseShape
        v-if="step !== 'tasks' && step !== 'questions'"
        shape="soft-boom"
        class="text-tertiary-container absolute -right-[7vw] bottom-[7vw] z-0 h-[20vw] min-h-[150px] w-[20vw] min-w-[120px] transition-all"
      />
      <div v-if="step === 'entry' || step === 'reenter'" class="flex flex-col gap-4">
        <p v-if="step === 'reenter'" class="text-body-large">
          {{ $t('portal.feedback.reenter') }}
        </p>
        <div class="flex flex-col gap-4">
          <div class="relative z-10 flex flex-col items-center gap-4">
            <AccessCodeInput
              v-model="codeInput"
              :has-error="showInvalidCode"
              autofocus
              :label="$t('portal.entry.codeLabel')"
              :disabled="isEntering || isSubmitting"
              @submit="submitCode"
            />
            <p v-if="showInvalidCode" class="text-body-large text-error text-center">
              {{ $t('portal.entry.invalid') }}
            </p>
            <p class="text-body-large text-on-surface-variant">
              {{ $t('portal.entry.description') }}
            </p>
            <BaseTextButton
              color="filled"
              size="L"
              @click="submitCode"
              :disabled="isEntering || isSubmitting"
            >
              {{ $t('portal.entry.submit') }}
            </BaseTextButton>
          </div>
        </div>
      </div>

      <div v-else-if="step === 'confirm'" class="flex flex-col items-center gap-4">
        <h1 class="text-display-large text-center">
          {{ $t('portal.confirm.greeting', { name: firstName }) }}
        </h1>
        <p class="text-headline-small text-on-surface-variant text-center">
          {{ $t('portal.confirm.question') }}
        </p>
        <p v-if="confirmError" class="text-body-medium text-error">
          {{ $t('portal.confirm.error') }}
        </p>
        <div class="mt-10 flex gap-2">
          <BaseTextButton
            size="L"
            icon="close"
            color="outlined"
            :disabled="isLoadingTasks || isEnding"
            @click="rejectName"
          >
            {{ $t('portal.confirm.no') }}
          </BaseTextButton>
          <BaseTextButton
            color="filled"
            size="L"
            icon="check"
            :disabled="isLoadingTasks || isEnding"
            @click="acceptName"
          >
            {{ $t('portal.confirm.yes') }}
          </BaseTextButton>
        </div>
      </div>

      <PortalSelfAssessment
        v-else-if="step === 'questions' && questionnaire && activeSurveyId"
        :questionnaire="questionnaire"
        :survey-id="activeSurveyId"
        :error-message="feedbackError ?? undefined"
        :submitting="isSubmitting"
        @back="backToTasks"
        @submit="submitCurrent"
      />

      <div v-else class="flex flex-col gap-2">
        <h1 class="text-display-small mb-4">
          {{ $t('portal.confirm.greeting', { name: firstName }) }}
        </h1>
        <h2 class="text-title-large">{{ $t('portal.tasks.title') }}</h2>
        <p v-if="tasksError" class="text-body-medium text-error">{{ tasksError }}</p>
        <p v-if="tasks.length === 0" class="text-body-large text-on-surface-variant">
          {{ $t('portal.tasks.empty') }}
        </p>
        <ul v-else class="mb-10 flex flex-col gap-0.5">
          <li
            v-for="task in tasks"
            :key="task.surveyId"
            class="first:rounded-t-large last:rounded-b-large overflow-hidden"
          >
            <button
              type="button"
              class="bg-surface-container group relative flex h-32 w-full flex-row items-center gap-4 overflow-hidden p-4 text-left disabled:opacity-50"
              :disabled="openingSurveyId !== null"
              @click="openTask(task.surveyId)"
            >
              <img
                :src="task.image"
                :alt="task.title"
                class="rounded-medium aspcet-4/3 h-full shrink-0"
              />
              <div class="flex grow flex-col gap-2">
                <span class="text-headline-small">{{ task.title }}</span>

                <div class="flex flex-row gap-1">
                  <span
                    class="bg-surface-container-lowest text-on-surface-variant rounded-small text-label-large inline-flex flex-row items-center gap-2 px-2 py-1"
                    >Gestartet am {{ formatStartedAt(task.startedAt) }}</span
                  >
                  <span
                    class="bg-surface-container-lowest text-on-surface-variant rounded-small text-label-large inline-flex flex-row items-center gap-2 px-2 py-1"
                    >Lehrperson: {{ task.teacher.firstName[0] }}. {{ task.teacher.lastName }}</span
                  >
                  <span
                    class="bg-surface-container-lowest text-on-surface-variant rounded-small text-label-large inline-flex flex-row items-center gap-2 px-2 py-1"
                    >{{ task.questionCount }} Fragen</span
                  >
                </div>
              </div>
              <div
                class="bg-primary text-on-primary flex h-20 w-20 shrink-0 items-center justify-center rounded-full p-2"
              >
                <BaseIcon size="40">arrow_forward</BaseIcon>
              </div>
              <BaseStateLayer />
            </button>
          </li>
        </ul>
        <div class="flex justify-center">
          <BaseTextButton
            color="tonalTertiary"
            icon="logout"
            size="L"
            :disabled="isEnding"
            @click="finish"
          >
            {{ $t('portal.tasks.done') }}
          </BaseTextButton>
        </div>
      </div>
    </div>
  </FullScreenLayoutContent>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import FullScreenLayoutContent from '@/core/components/layouts/FullScreenLayoutContent.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import AccessCodeInput from '../components/AccessCodeInput.vue'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import PortalSelfAssessment from '../components/PortalSelfAssessment.vue'
  import type {
    PortalEnterFailure,
    PortalSelfAssessment as PortalSelfAssessmentModel,
    PortalSubmitResult,
    PortalTask,
  } from '../models/portal-session'
  import { usePortalSession } from '../composables/use-portal-session'
  import { usePortalTasks } from '../composables/use-portal-tasks'
  import {
    clearAllPortalDrafts,
    clearPortalDraft,
    readPortalDraft,
  } from '../composables/use-portal-draft'
  import { buildSelfAssessmentBody } from '../utils/self-assessment-body'
  import BaseShape from '@/core/components/base/shapes/BaseShape.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'
  import LumiaLogo from '@/core/components/LumiaLogo.vue'

  type Step = 'entry' | 'confirm' | 'tasks' | 'questions' | 'reenter'

  const route = useRoute()
  const { locale, t } = useI18n()
  const portalLinkToken = computed(() => route.params.portalLinkToken as string)
  const { enterStatus, endStatus, enterWithCode, endVisit, clearSession } =
    usePortalSession(portalLinkToken)
  const { sessionStatus, submitStatus, loadTasks, loadQuestionnaire, submitSelfAssessment } =
    usePortalTasks()

  const step = ref<Step>('entry')
  const codeInput = ref<string>()
  const firstName = ref('')
  const tasks = ref<PortalTask[]>([])
  const entryError = ref<Exclude<PortalEnterFailure, 'empty'> | 'ended' | null>(null)
  const confirmError = ref(false)
  const tasksError = ref<string | null>(null)
  const feedbackError = ref<string | null>(null)
  const activeSurveyId = ref<string | null>(null)
  const questionnaire = ref<PortalSelfAssessmentModel | null>(null)
  const openingSurveyId = ref<string | null>(null)
  const submitLock = ref(false)

  const isEntering = computed(() => enterStatus.value === ApiRequestStatus.Loading)
  const isLoadingTasks = computed(() => sessionStatus.value === ApiRequestStatus.Loading)
  const isEnding = computed(() => endStatus.value === ApiRequestStatus.Loading)
  const isSubmitting = computed(
    () => submitLock.value || submitStatus.value === ApiRequestStatus.Loading,
  )
  const showInvalidCode = computed(() => entryError.value != null && entryError.value !== 'ended')

  watch(codeInput, () => {
    entryError.value = null
  })

  const resetToEntry = () => {
    step.value = 'entry'
    codeInput.value = undefined
    firstName.value = ''
    tasks.value = []
    confirmError.value = false
    tasksError.value = null
    feedbackError.value = null
    activeSurveyId.value = null
    questionnaire.value = null
    openingSurveyId.value = null
  }

  watch(portalLinkToken, () => {
    clearAllPortalDrafts()
    clearSession()
    entryError.value = null
    resetToEntry()
  })

  const showTasks = async () => {
    const tasksResult = await loadTasks()
    if (tasksResult.ok) {
      firstName.value = tasksResult.firstName
      tasks.value = tasksResult.tasks
      tasksError.value = null
      step.value = 'tasks'
      return
    }
    if (tasksResult.reason === 'ended') {
      entryError.value = 'ended'
      resetToEntry()
      return
    }
    tasksError.value = t('portal.entry.error')
    step.value = 'tasks'
  }

  const returnToTasks = async (surveyId: string | null) => {
    if (surveyId) {
      clearPortalDraft(surveyId)
    }
    activeSurveyId.value = null
    questionnaire.value = null
    feedbackError.value = null
    await showTasks()
  }

  const applySubmitResult = async (result: PortalSubmitResult, fromReenter = false) => {
    const surveyId = activeSurveyId.value
    if (result.ok) {
      await returnToTasks(surveyId)
      return
    }
    if (result.reason === 'done') {
      await returnToTasks(surveyId)
      return
    }
    if (result.reason === 'notFound') {
      if (fromReenter) {
        clearAllPortalDrafts()
        await returnToTasks(null)
        return
      }
      await returnToTasks(surveyId)
      return
    }
    if (result.reason === 'ended') {
      feedbackError.value = null
      entryError.value = 'ended'
      codeInput.value = undefined
      step.value = 'reenter'
      return
    }
    if (result.reason === 'validation') {
      feedbackError.value = result.message || t('portal.entry.error')
      step.value = 'questions'
      return
    }
    feedbackError.value = t('portal.entry.error')
    step.value = 'questions'
  }

  const submitCode = async () => {
    entryError.value = null
    const resuming = step.value === 'reenter'
    const result = await enterWithCode(codeInput.value ?? '')
    if (!result.ok) {
      if (result.reason !== 'empty') {
        entryError.value = result.reason
      }
      return
    }
    firstName.value = result.firstName
    codeInput.value = undefined
    if (resuming) {
      await submitCurrent(true)
      return
    }
    step.value = 'confirm'
  }

  const acceptName = async () => {
    confirmError.value = false
    const result = await loadTasks()
    if (result.ok) {
      firstName.value = result.firstName
      tasks.value = result.tasks
      step.value = 'tasks'
      return
    }
    if (result.reason === 'ended') {
      entryError.value = 'ended'
      resetToEntry()
      return
    }
    confirmError.value = true
  }

  const leave = async () => {
    clearAllPortalDrafts()
    await endVisit()
    entryError.value = null
    resetToEntry()
  }

  const rejectName = () => leave()
  const finish = () => leave()

  const openTask = async (surveyId: string) => {
    if (openingSurveyId.value) {
      return
    }
    openingSurveyId.value = surveyId
    tasksError.value = null
    feedbackError.value = null
    try {
      const result = await loadQuestionnaire(surveyId)
      if (openingSurveyId.value !== surveyId) {
        return
      }
      if (result.ok) {
        activeSurveyId.value = surveyId
        questionnaire.value = result.questionnaire
        step.value = 'questions'
        return
      }
      if (result.reason === 'ended') {
        entryError.value = 'ended'
        resetToEntry()
        return
      }
      if (result.reason === 'notFound') {
        clearPortalDraft(surveyId)
        await showTasks()
        return
      }
      tasksError.value = t('portal.entry.error')
    } finally {
      if (openingSurveyId.value === surveyId) {
        openingSurveyId.value = null
      }
    }
  }

  const backToTasks = () => {
    feedbackError.value = null
    step.value = 'tasks'
  }

  const submitCurrent = async (fromReenter = false) => {
    if (submitLock.value) {
      return
    }
    if (!questionnaire.value || !activeSurveyId.value) {
      await showTasks()
      return
    }
    const body = buildSelfAssessmentBody(questionnaire.value, readPortalDraft(activeSurveyId.value))
    if (!body) {
      feedbackError.value = t('portal.feedback.incomplete')
      step.value = 'questions'
      return
    }
    submitLock.value = true
    try {
      const result = await submitSelfAssessment(activeSurveyId.value, body)
      await applySubmitResult(result, fromReenter)
    } finally {
      submitLock.value = false
    }
  }

  const formatStartedAt = (startedAt: string) => {
    const date = new Date(startedAt)
    if (Number.isNaN(date.getTime())) {
      return startedAt
    }
    return new Intl.DateTimeFormat(locale.value, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date)
  }
</script>
